import React, { useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import AiSummaryModal from './AiSummaryModal';

const SUMMARY_PERIODS = [
    'Today',
    'This Week',
    'Last Week',
    'This Month',
    'Last Month',
    'Previous Month',
    'Custom Range',
];

export default function AiWorkSummaryCard({
    summaryData,
    loading = false,
    period = 'This Week',
    onPeriodChange,
    onRefresh,
    onGenerate,
    onTaskClick,
    onProjectClick,
    onMilestoneClick,
    onSprintClick,
}) {
    const [localPeriod, setLocalPeriod] = useState('This Week');
    const [customRange, setCustomRange] = useState(null);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const activePeriod = period || localPeriod;

    const handlePeriodSelect = (newPeriod) => {
        setLocalPeriod(newPeriod);
        if (newPeriod !== 'Custom Range') {
            setCustomRange(null);
            if (onPeriodChange) {
                onPeriodChange(newPeriod);
            }
        }
    };

    const handleRangeChange = (dates) => {
        setCustomRange(dates);
        if (dates && dates[0] && dates[1] && onPeriodChange) {
            onPeriodChange('Custom Range', {
                startDate: dates[0].format('YYYY-MM-DD'),
                endDate: dates[1].format('YYYY-MM-DD'),
            });
        }
    };

    // Truthful metrics derived from summaryData
    const activeTasks = summaryData?.activeTaskCount ?? 0;
    const projectCount = summaryData?.projectCount ?? 0;
    const inProgressCount = summaryData?.inProgressCount ?? 0;
    const underReviewCount = summaryData?.underReviewCount ?? 0;

    // Format timestamp per Section 4: "Generated Sep 18, 2026, 10:05 AM"
    const formattedTimestamp = summaryData?.generatedAt
        ? `Generated ${dayjs(summaryData.generatedAt).format('MMM D, YYYY, h:mm A')}`
        : `Updated ${summaryData?.updatedTimeAgo || 'Just now'}`;

    const bullets = Array.isArray(summaryData?.bullets) && summaryData.bullets.length > 0
        ? summaryData.bullets
        : null;

    return (
        <>
            <div
                id="ai-work-summary-card"
                className="relative bg-gradient-to-r from-blue-50/70 via-white to-sky-50/60 dark:from-[#1a2234] dark:via-[#1e293b] dark:to-[#0f172a] border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-5 shadow-xs transition-all duration-300"
            >
                {/* Header matching specification */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-blue-100/70 dark:border-blue-900/40 pb-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-blue-600/10 text-blue-700 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                            <span className="material-symbols-outlined text-lg leading-none">auto_awesome</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <h2 className="text-base font-bold text-[#003263] dark:text-[#a7c8ff] font-headline tracking-tight">
                                AI Work Summary
                            </h2>
                            <div className="relative inline-block">
                                <select
                                    value={activePeriod}
                                    onChange={(e) => handlePeriodSelect(e.target.value)}
                                    className="bg-white/90 dark:bg-[#1e293b] border border-blue-200 dark:border-slate-700 rounded-lg text-xs font-bold text-blue-900 dark:text-blue-300 py-1 pl-2.5 pr-7 focus:ring-2 focus:ring-blue-500 cursor-pointer shadow-xs"
                                >
                                    {SUMMARY_PERIODS.map((p) => (
                                        <option key={p} value={p}>
                                            {p}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Custom Range Picker */}
                            {activePeriod === 'Custom Range' && (
                                <DatePicker.RangePicker
                                    size="small"
                                    value={customRange}
                                    onChange={handleRangeChange}
                                    className="text-xs"
                                />
                            )}
                        </div>

                        {/* Generated Timestamp per Section 4 */}
                        <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1 font-medium">
                            <span className="material-symbols-outlined text-xs text-blue-500 leading-none">schedule</span>
                            {formattedTimestamp}
                        </span>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5">
                        <button
                            type="button"
                            onClick={onGenerate || onRefresh}
                            className="px-2.5 py-1 text-xs font-bold text-blue-700 dark:text-blue-300 bg-white/80 dark:bg-slate-800 hover:bg-blue-50 border border-blue-200/70 dark:border-slate-700 rounded-lg shadow-2xs transition-all flex items-center gap-1 cursor-pointer"
                            title="Generate Summary"
                        >
                            <span className={`material-symbols-outlined text-sm leading-none ${loading ? 'animate-spin' : ''}`}>
                                bolt
                            </span>
                            <span>Generate Summary</span>
                        </button>
                        <button
                            type="button"
                            onClick={onRefresh}
                            className="p-1.5 text-slate-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all cursor-pointer"
                            title="Refresh Summary"
                        >
                            <span className={`material-symbols-outlined text-base ${loading ? 'animate-spin' : ''}`}>
                                refresh
                            </span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(true)}
                            className="p-1.5 text-slate-500 hover:text-blue-700 dark:hover:text-blue-300 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all cursor-pointer"
                            title="Open Full Summary Modal"
                        >
                            <span className="material-symbols-outlined text-base">open_in_new</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="p-1.5 text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-all ml-1 cursor-pointer"
                            title={isExpanded ? 'Collapse Summary' : 'Expand Summary'}
                        >
                            <span className="material-symbols-outlined text-base">
                                {isExpanded ? 'expand_less' : 'expand_more'}
                            </span>
                        </button>
                    </div>
                </div>

                {/* Narrative Briefings Body */}
                {isExpanded && (
                    <div className="py-3.5 space-y-2.5 text-xs text-slate-700 dark:text-slate-300 leading-relaxed">
                        {bullets ? (
                            bullets.map((bullet, idx) => {
                                const isAmber = bullet.pillColor === 'amber' || bullet.type === 'variance' || (bullet.pillText && bullet.pillText.toLowerCase().includes('overdue')) || bullet.type === 'deadline_change';
                                const isSlate = bullet.pillColor === 'slate' || bullet.type === 'sprint';
                                const dotColor = isAmber ? 'bg-amber-500' : isSlate ? 'bg-slate-400 dark:bg-slate-500' : 'bg-blue-600';

                                const textContent = bullet.taskName && bullet.text?.startsWith(bullet.taskName)
                                    ? bullet.text.slice(bullet.taskName.length).trim()
                                    : bullet.text;

                                return (
                                    <div key={idx} className="flex items-start gap-2.5">
                                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dotColor} mt-2 flex-shrink-0`} />
                                        <div className="flex-1">
                                            {/* Clickable Task Link */}
                                            {bullet.taskName ? (
                                                bullet.taskId ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => onTaskClick?.(bullet.taskId)}
                                                        className="font-bold text-slate-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline cursor-pointer mr-1"
                                                    >
                                                        {bullet.taskName}
                                                    </button>
                                                ) : (
                                                    <strong className="text-slate-900 dark:text-white mr-1">{bullet.taskName}</strong>
                                                )
                                            ) : null}

                                            {/* Clickable Project Link if attached */}
                                            {bullet.projectName && onProjectClick && (
                                                <button
                                                    type="button"
                                                    onClick={() => onProjectClick(bullet.projectId || bullet.projectName)}
                                                    className="font-bold text-blue-700 dark:text-blue-400 hover:underline cursor-pointer mr-1"
                                                >
                                                    [{bullet.projectName}]
                                                </button>
                                            )}

                                            <span>{textContent}</span>

                                            {/* Pill / Tag with potential sprint/filter click */}
                                            {bullet.pillText && !textContent?.includes(bullet.pillText) && (
                                                <span
                                                    onClick={() => {
                                                        if (bullet.sprintName && onSprintClick) {
                                                            onSprintClick(bullet.sprintName);
                                                        }
                                                    }}
                                                    className={`ml-1.5 font-semibold text-[11px] px-1.5 py-0.5 rounded border inline-block ${
                                                        bullet.sprintName ? 'cursor-pointer hover:underline' : ''
                                                    } ${
                                                        isAmber
                                                            ? 'text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800'
                                                            : isSlate
                                                            ? 'text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                                                            : 'text-blue-700 dark:text-blue-300 bg-blue-100/60 dark:bg-blue-900/40 border-blue-200/50 dark:border-blue-800'
                                                    }`}
                                                >
                                                    {bullet.pillText}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                        ) : activeTasks === 0 ? (
                            <div className="space-y-2 text-xs text-slate-600 dark:text-slate-400">
                                <div className="flex items-start gap-2.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                    <div className="flex-1">
                                        You currently have <strong className="text-slate-900 dark:text-white">no active tasks</strong> assigned for this week across any project.
                                    </div>
                                </div>
                                <div className="flex items-start gap-2.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                    <div className="flex-1">
                                        All queues are clear. When tasks are assigned to you, your work intelligence briefing will update here automatically.
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2.5 text-xs text-slate-700 dark:text-slate-300">
                                <div className="flex items-start gap-2.5">
                                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                    <div className="flex-1">
                                        You currently have <strong className="text-slate-900 dark:text-white">{activeTasks} active task{activeTasks === 1 ? '' : 's'}</strong> across {projectCount} project{projectCount === 1 ? '' : 's'}.
                                    </div>
                                </div>

                                {(inProgressCount > 0 || underReviewCount > 0) && (
                                    <div className="flex items-start gap-2.5">
                                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
                                        <div className="flex-1">
                                            <span className="font-semibold text-blue-700 dark:text-blue-300 bg-blue-100/60 dark:bg-blue-900/40 px-1.5 py-0.5 rounded border border-blue-200/50 dark:border-blue-800">
                                                {inProgressCount} tasks
                                            </span>{' '}
                                            are In Progress, while{' '}
                                            <span className="font-semibold text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded border border-purple-200 dark:border-purple-800">
                                                {underReviewCount} tasks
                                            </span>{' '}
                                            are currently Under Review.
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Footer matching specification */}
                <div className="flex items-center justify-between pt-3 border-t border-blue-100/70 dark:border-blue-900/40">
                    <button
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="px-3.5 py-1.5 text-xs font-bold text-white rounded-xl shadow-xs hover:opacity-90 transition-all flex items-center gap-1.5 cursor-pointer"
                        style={{ background: 'linear-gradient(90deg, #03488A 0%, #00AAF1 100%)' }}
                    >
                        <span className="material-symbols-outlined text-sm">description</span> View Full Summary
                    </button>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onRefresh}
                            className="text-xs font-bold text-blue-700 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-200 flex items-center gap-1 px-2.5 py-1 rounded-lg hover:bg-white dark:hover:bg-slate-800 transition-all cursor-pointer"
                        >
                            <span className={`material-symbols-outlined text-sm ${loading ? 'animate-spin' : ''}`}>
                                autorenew
                            </span>
                            Refresh Summary
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal */}
            <AiSummaryModal
                visible={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                summaryData={summaryData}
                period={activePeriod}
                onTaskClick={onTaskClick}
                onProjectClick={onProjectClick}
                onMilestoneClick={onMilestoneClick}
                onSprintClick={onSprintClick}
            />
        </>
    );
}
